import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageContainer from "src/components/page/PageContainer"
import CustomTextarea from "../text/CustomTextarea"
import { Button, DialogContentText, Stack } from "@mui/material"
import axios from "axios"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { useResponsive } from "src/utils/useResponsive"
import { appBarHeightPixels } from "src/providers/AppProviders"
import TwoOptionsDialog from "../file/TwoOptionsDialog"

export async function sendSupportTicket(ticket: SupportTicket) {
  await axios.post("/api/wfl/workflow/support/ticket", ticket)
}

export type SupportTicketTag = "OTHER"

export type SupportTicketType = "PROBLEMS"

export interface SupportTicket {
  type: SupportTicketType
  title: string
  description: string
  tags: SupportTicketTag[]
  browserInfo: string
  version: string
}

interface ProblematicTablet {
  label: string
  value: string
}

const problematicTablet = [
  { label: "ASK", value: "ASK" },
  { label: "CREATE", value: "CREATE" },
  { label: "AI Text Translation", value: "TEXT" },
  { label: "AI File Translation", value: "FILE" },
  { label: "Editor", value: "EDITOR" },
  { label: "Other", value: "OTHER" }
]

function useWindowHeight() {
  const [height, setHeight] = useState(window.innerHeight)

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight)
    window.addEventListener("resize", handleResize)

    // Cleanup
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return height
}

const HelpPage: React.FC = () => {
  const height = useWindowHeight()
  const navigate = useNavigate()
  const [description, setDescription] = useState("")
  const [referingToTablet, setReferingToTablet] = useState<ProblematicTablet>({ label: "Other", value: "OTHER" })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const { isMobile } = useResponsive()
  const footerHeight = 68.5
  const stackPadding = 32
  const padding = 56
  const lineHeight = 23
  const selectionHeight = 72
  const rows = (height - appBarHeightPixels - selectionHeight - stackPadding - footerHeight - padding) / lineHeight

  const handleSendTicket = async () => {
    setIsSending(true)
    try {
      await sendSupportTicket({
        type: "PROBLEMS",
        tags: ["OTHER"],
        browserInfo: navigator.userAgent,
        version: process.env.REACT_APP_VERSION || "",
        title: "Issue with " + referingToTablet.label,
        description: description
      })
      setDialogOpen(true)
    } catch (error) {
      console.error("Failed to send support ticket:", error)
    } finally {
      setIsSending(false)
    }
  }

  const clearForm = () => {
    setDescription("")
    setReferingToTablet({ label: "Other", value: "OTHER" })
  }

  return (
    <PageContainer>
      <Stack spacing={{ xs: 2, md: 4 }} sx={{ p: { xs: 2, md: 6, lg: 8 }, pb: { xs: 4 }, flex: 1, minHeight: 0 }}>
        <CustomAutocomplete
          options={problematicTablet}
          getOptionLabel={(option) => option.label}
          onChange={(e, val) => setReferingToTablet(val as ProblematicTablet)}
          label="Referring to tablet"
          value={referingToTablet}
          isOptionEqualToValue={(o1, o2) => o1.value === o2.value}
          disableClearable
        />
        <CustomTextarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={isMobile ? rows : 12}
          placeholder="Describe your issue"
        />
        <Button
          variant="contained"
          color="primary"
          disabled={description.length === 0 || isSending}
          onClick={handleSendTicket}
        >
          {isSending ? "Sending..." : "Send Support Ticket"}
        </Button>
      </Stack>

      <TwoOptionsDialog
        open={dialogOpen}
        color="primary"
        title="Thank you!"
        content={
          <DialogContentText>
            Your support ticket has been submitted. Our team will review it and get back to you soon.
          </DialogContentText>
        }
        firstOptionText="Send another ticket"
        secondOptionText="Go back home"
        firstOptionAction={() => {
          setDialogOpen(false)
          clearForm()
        }}
        secondOptionAction={() => {
          setDialogOpen(false)
          navigate("/")
        }}
      />
    </PageContainer>
  )
}

export default HelpPage
